import { z } from "zod";

import { LIMITS, USER_ROLES } from "../constants.js";
import {
  emailAddressSchema,
  isoUtcSchema,
  mailboxIdSchema,
  sessionIdSchema,
  userIdSchema,
} from "./common.js";

/* -------------------------------------------------------------------------- */
/* Credentials                                                                 */
/* -------------------------------------------------------------------------- */

/** Signup/login password policy (length is the only strength signal here). */
export const passwordSchema = z.string().min(12).max(256);
export const loginPasswordSchema = z.string().min(1).max(256);

/** Six-digit TOTP code (RFC 6238). */
export const totpCodeSchema = z.string().regex(/^\d{6}$/, "TOTP codes are six digits");

/** Opaque, short-lived MFA challenge identifier. */
export const challengeIdSchema = z.string().min(16).max(128);

/* -------------------------------------------------------------------------- */
/* Requests                                                                    */
/* -------------------------------------------------------------------------- */

export const signupRequestSchema = z.object({
  email: emailAddressSchema,
  displayName: z.string().min(1).max(LIMITS.maxDisplayNameLength),
  password: passwordSchema,
  turnstileToken: z.string().min(1).max(4096),
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

export const loginRequestSchema = z.object({
  email: emailAddressSchema,
  password: loginPasswordSchema,
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const totpVerifyRequestSchema = z.object({
  challengeId: challengeIdSchema,
  code: totpCodeSchema,
});
export type TotpVerifyRequest = z.infer<typeof totpVerifyRequestSchema>;

export const totpEnableRequestSchema = z.object({ code: totpCodeSchema });
export type TotpEnableRequest = z.infer<typeof totpEnableRequestSchema>;

export const totpDisableRequestSchema = z.object({
  code: totpCodeSchema,
  password: loginPasswordSchema,
});
export type TotpDisableRequest = z.infer<typeof totpDisableRequestSchema>;

/** WebAuthn credential payloads validated structurally, then by the library. */
export const webauthnCredentialSchema = z.object({
  id: z.string().min(1),
  rawId: z.string().min(1),
  type: z.literal("public-key"),
  authenticatorAttachment: z.string().optional(),
  response: z.record(z.string(), z.unknown()),
  clientExtensionResults: z.record(z.string(), z.unknown()).optional(),
});
export type WebauthnCredential = z.infer<typeof webauthnCredentialSchema>;

export const passkeyRegisterVerifyRequestSchema = z.object({
  response: webauthnCredentialSchema,
  name: z.string().min(1).max(64).optional(),
});
export type PasskeyRegisterVerifyRequest = z.infer<typeof passkeyRegisterVerifyRequestSchema>;

export const passkeyAuthenticationOptionsRequestSchema = z.object({
  email: emailAddressSchema.optional(),
});
export type PasskeyAuthenticationOptionsRequest = z.infer<
  typeof passkeyAuthenticationOptionsRequestSchema
>;

export const passkeyAuthenticateVerifyRequestSchema = z.object({
  response: webauthnCredentialSchema,
});
export type PasskeyAuthenticateVerifyRequest = z.infer<
  typeof passkeyAuthenticateVerifyRequestSchema
>;

/* -------------------------------------------------------------------------- */
/* Resources                                                                   */
/* -------------------------------------------------------------------------- */

export const userRoleSchema = z.enum([...USER_ROLES]);

export const userPublicSchema = z.object({
  id: userIdSchema,
  email: emailAddressSchema,
  displayName: z.string().min(1).max(LIMITS.maxDisplayNameLength),
  role: userRoleSchema,
  totpEnabled: z.boolean(),
  passkeyCount: z.number().int().nonnegative(),
  createdAt: isoUtcSchema,
  mailboxIds: z.array(mailboxIdSchema),
});
export type UserPublic = z.infer<typeof userPublicSchema>;

export const sessionSchema = z.object({
  id: sessionIdSchema,
  createdAt: isoUtcSchema,
  expiresAt: isoUtcSchema,
  ip: z.string().max(64).nullable(),
  userAgent: z.string().max(512).nullable(),
  current: z.boolean(),
});
export type Session = z.infer<typeof sessionSchema>;

export const listSessionsResponseSchema = z.object({ sessions: z.array(sessionSchema) });
export type ListSessionsResponse = z.infer<typeof listSessionsResponseSchema>;

export const passkeySchema = z.object({
  id: z.string().min(1).max(512),
  name: z.string().min(1).max(64),
  createdAt: isoUtcSchema,
  lastUsedAt: isoUtcSchema.nullable(),
});
export type Passkey = z.infer<typeof passkeySchema>;

export const listPasskeysResponseSchema = z.object({ passkeys: z.array(passkeySchema) });
export type ListPasskeysResponse = z.infer<typeof listPasskeysResponseSchema>;

export const authSessionResponseSchema = z.object({
  user: userPublicSchema,
  session: sessionSchema,
  csrfToken: z.string().min(16),
});
export type AuthSessionResponse = z.infer<typeof authSessionResponseSchema>;

export const loginResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("authenticated"), session: authSessionResponseSchema }),
  z.object({ status: z.literal("totp_required"), challengeId: challengeIdSchema }),
]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const totpSetupResponseSchema = z.object({
  secret: z.string().min(16),
  otpauthUrl: z.string().min(1),
});
export type TotpSetupResponse = z.infer<typeof totpSetupResponseSchema>;

export const passkeyRegistrationOptionsSchema = z.object({
  options: z.record(z.string(), z.unknown()),
});
export type PasskeyRegistrationOptions = z.infer<typeof passkeyRegistrationOptionsSchema>;

export const passkeyAuthenticationOptionsSchema = z.object({
  options: z.record(z.string(), z.unknown()),
});
export type PasskeyAuthenticationOptions = z.infer<typeof passkeyAuthenticationOptionsSchema>;

export const revokeAllSessionsResponseSchema = z.object({
  ok: z.literal(true),
  revoked: z.number().int().nonnegative(),
});
export type RevokeAllSessionsResponse = z.infer<typeof revokeAllSessionsResponseSchema>;
